import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, BookOpen, GitCompare, X, Goal, Send } from 'lucide-react';
import { getTechnicalKpiBreakdown, getTechnicalStats } from '../api';
import PageChrome from '../components/PageChrome';
import MultiSelect from '../components/MultiSelect';
import { TECHNICAL_KPI_MAPPING } from './gps/technicalKpiMapping';

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: '28day', label: '28-Day' },
  { value: 'preseason', label: 'Pre-season' },
  { value: 'season', label: 'Season' },
];

// Confirmed 2026-07-23: one table per position (app's internal convention, not SciSports's raw
// labels), not a single mega-table like TeamKpiOverview.js's Tactical/Identity page — Technical's
// KPI set genuinely differs per position, so a shared column set doesn't work here. GK is
// omitted: no Technical KPIs are seeded for GK (kpi_definitions has zero 'technical' rows with
// position='GK' — same gap as Physical's GK exclusion), so there's nothing to build a table from.
const POSITIONS = ['CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];
const ACCENT = '#f59e0b';

// Same NULL-to-bottom-regardless-of-direction comparator as Team360Table.js (Stage 4) /
// TeamKpiOverview.js (Stage 6a) — not Availability.js's `?? -1` comparator, which only sorts
// nulls last when descending. Each sortable table in this codebase keeps its own copy
// (existing convention) rather than a shared util.
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}

function SortHeader({ label, sortKey, activeKey, dir, onClick, align, title }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    // Header labels wrap (2026-08-17) instead of nowrap: a single unbreakable
    // "PROGRESSIVE PASSES" was ~150px on its own and, times 8-10 KPI columns, forced the whole
    // table into horizontal scroll. Letting the longest labels take two lines is what makes a
    // position's full column set fit one screen — the header row is a fixed cost paid once,
    // whereas the scroll was paid on every read.
    <th onClick={() => onClick(sortKey)} title={title} style={{ textAlign: align || 'left', padding: '8px 9px', fontSize: 10, fontWeight: 700, color: active ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.02em', cursor: 'pointer', userSelect: 'none', verticalAlign: 'bottom', lineHeight: 1.25 }}>
      <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 3, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        <span>{label}</span><Icon size={10} style={{ flexShrink: 0, marginBottom: 1 }} />
      </span>
    </th>
  );
}

const numCell = { padding: '9px 9px', textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

export function ScoreCell({ value, bold }) {
  if (value == null) return <td style={{ ...numCell, color: 'var(--text-dimmed)' }}>no data</td>;
  return <td style={{ ...numCell, fontWeight: bold ? 700 : 400 }}>{Math.round(value)}</td>;
}

// KPI cells (2026-07-29): rate KPIs show the rate alongside its attempt count — "80% (20)" —
// since a rate on its own hides the sample size (100% on 1 attempt reads very differently from
// 100% on 20). Volume KPIs show the plain raw count, no scaling — the ×100-placeholder-scaled
// number (e.g. 1600 for a raw count of 16) was never meaningful, just inflated-looking; the
// underlying `value`/scoring formula is untouched, this is display-only (see pillarScores.js's
// computeTechnicalBreakdown comment).
export function KpiCell({ value, count, scoringMode }) {
  if (value == null) return <td style={{ ...numCell, color: 'var(--text-dimmed)' }}>no data</td>;
  // Bonus KPIs show their raw count, same as volume — `value` for them is count × points-per-unit,
  // which is a score contribution, not a percentage, and rendering it as "%" would be nonsense.
  if (scoringMode === 'volume' || scoringMode === 'bonus') return <td style={numCell}>{Math.round(count)}</td>;
  return (
    <td style={numCell}>
      {Math.round(value)}%
      <span style={{ color: 'var(--text-muted)', marginLeft: 4, fontSize: 11 }}>({Math.round(count)})</span>
    </td>
  );
}

// "Definitions & Mapping" modal (2026-07-30, rebuilt 2026-08-17 for the KNVB switch) — shows,
// per HeraLabX Technical KPI, which raw KNVB player-stats CSV field(s) it's built from and a
// plain-English description. Used to show SciSports's own field names/glossary — KNVB fully
// replaced SciSports as the Technical Stats source (2026-08-17), so this now mirrors
// technicalImport.js's KNVB_KPI_FIELD_MAP one-to-one (see technicalKpiMapping.js). Rate KPIs
// are annotated with the actual formula HeraLabX applies (computeKnvbRate) — KNVB, unlike
// SciSports, never delivers a pre-computed rate column, so this IS the real formula, not
// context-only.
function DefinitionsModal({ onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }} onClick={onClose}>
      <div
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 80px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Definitions &amp; Mapping</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>Each HeraLabX KPI, the raw KNVB field(s) it's built from, and how the score is calculated.</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '10px 22px 22px' }}>
          {TECHNICAL_KPI_MAPPING.map(k => (
            <div key={k.kpiName} style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: ACCENT, marginBottom: 6 }}>{k.kpiName}</div>

              {!k.fields && (
                <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>{k.definition}</div>
              )}
              {k.fields && (
                <>
                  <div style={{ fontSize: 12.5, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-app)', color: 'var(--text-secondary)', padding: '2px 6px', borderRadius: 4, marginRight: 4 }}>
                      {k.fields.join(' + ')}
                    </span>
                    {k.successFields && (
                      <>
                        <span style={{ color: 'var(--text-dimmed)' }}> / </span>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-app)', color: 'var(--text-secondary)', padding: '2px 6px', borderRadius: 4 }}>
                          {k.successFields.join(' + ')}
                        </span>
                      </>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{k.definition}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 4 }}>
                    {k.successFields
                      ? `Rate = ${k.successFields.join(' + ')} ÷ ${k.fields.join(' + ')} × 100`
                      : 'Volume-scored — no success/fail split, shown as a raw count (see the * note on the tables).'}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Visual treatment per bonus KPI. Goals/Assists keep the icons they've had since 2026-08-02;
// the three added 2026-08-17 use short text marks instead — there's no icon that reads as
// "key pass" or "underlap" at 11px, and an ambiguous glyph is worse than an abbreviation.
const BONUS_STYLE = {
  'Goals':            { icon: Goal, color: '#f59e0b' },
  'Assists':          { icon: Send, color: '#3b82f6' },
  'key passes':       { abbr: 'KP',  color: '#14b8a6' },
  'Block shots':      { abbr: 'BS',  color: '#a855f7' },
  'Block crosses':    { abbr: 'BC',  color: '#c084fc' },
  // '2B' rather than 'SB' — too easily misread as Block shots' 'BS' at 9.5px.
  'Second balls':     { abbr: '2B',  color: '#38bdf8' },
  'Underlap/overlap': { abbr: 'U/O', color: '#64748b' },
};
// Shown next to the player name instead of as a column — see the kpiCols comment.
const NAME_BADGE_KPIS = new Set(['Goals', 'Assists']);
// Singular unit for the legend ("+15 per goal", not "+15 per Goals").
const BONUS_UNIT = {
  'Goals': 'goal', 'Assists': 'assist', 'key passes': 'key pass',
  'Block shots': 'blocked shot', 'Block crosses': 'blocked cross',
  'Second balls': 'second ball', 'Underlap/overlap': 'underlap/overlap',
};

function BonusBadges({ bonuses }) {
  if (!bonuses || !bonuses.length) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginLeft: 8, verticalAlign: 'middle' }}>
      {bonuses.map(b => {
        const st = BONUS_STYLE[b.name] || { abbr: b.name.slice(0, 2).toUpperCase(), color: 'var(--text-muted)' };
        const Icon = st.icon;
        // Deliberately states the per-unit weight rather than count × weight as a single "+N
        // Technical" figure. Over a multi-match window the composite is the average of each
        // match's own score (see computeTechnicalBreakdown), so the window-total bonus is
        // divided by matches played — quoting the raw total would overstate the real effect
        // several-fold (e.g. 72 underlap runs across 6 matches contributes ~12, not 72).
        return (
          <span key={b.kpiId}
            title={`${b.count} ${b.name} over this period · +${b.pointsPerUnit ?? '?'} Technical each, averaged across matches played`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color: st.color, whiteSpace: 'nowrap' }}>
            {Icon ? <Icon size={12} /> : <span style={{ fontSize: 9.5, letterSpacing: '0.02em' }}>{st.abbr}</span>}
            {b.count}
          </span>
        );
      })}
    </span>
  );
}

// One table per position — independent sort state per table, since each has its own column set.
function PositionTable({ position, players }) {
  const [sortKey, setSortKey] = useState('pillarScore');
  const [sortDir, setSortDir] = useState('desc');

  // Goals/Assists are match events rather than per-position KPIs — they're not on the club's
  // per-position KPI sheet, and they apply to every outfield position identically — so they stay
  // as badges next to the player's name. Every other KPI keeps its own column, bonus-scored or
  // not: the column set per position is the club's own definition (see the position KPI sheet)
  // and must not change just because a KPI's scoring method did. Corrected 2026-08-17 — briefly
  // all bonus KPIs were pulled out of the table, which silently dropped key passes from CB/CDM
  // and underlap/overlap from FB.
  const kpiCols = (players[0]?.kpis || [])
    .filter(k => !NAME_BADGE_KPIS.has(k.kpiName))
    .map(k => ({ kpiId: k.kpiId, kpiName: k.kpiName, scoringMode: k.scoringMode, pointsPerUnit: k.pointsPerUnit }));

  // Points-per-unit per bonus KPI, read off the same benchmark_pct the backend scores with, so
  // the tooltip can never drift from the real weighting.
  const bonusKpis = (players[0]?.kpis || []).filter(k => k.scoringMode === 'bonus');

  const flatRows = useMemo(() => players.map(r => {
    const flat = { player_id: r.player_id, name: r.name, pillarScore: r.pillarScore };
    // Sorting stays keyed on `value` for every KPI (rate or volume) — the count field is
    // display-only, and for volume KPIs `value` is still a fixed positive multiple of count
    // (count × 100), so sorting by value already produces the identical order as sorting by
    // count would; no separate sort key needed.
    r.kpis.forEach(k => { flat[`kpi_${k.kpiId}`] = k.value; flat[`kpi_${k.kpiId}_count`] = k.count; });
    // Every bonus KPI the player actually registered, with the points it contributed. `value`
    // is already count × points-per-unit server-side, so the badge never re-derives the weight.
    flat.bonuses = (r.kpis || [])
      .filter(k => NAME_BADGE_KPIS.has(k.kpiName) && k.scoringMode === 'bonus' && (Number(k.count) || 0) > 0)
      .map(k => ({ kpiId: k.kpiId, name: k.kpiName, count: Number(k.count) || 0, pointsPerUnit: k.pointsPerUnit }));
    return flat;
  }), [players]);

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortedRows = useMemo(
    () => [...flatRows].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [flatRows, sortKey, sortDir]
  );

  const hasVolumeCols = kpiCols.some(k => k.scoringMode === 'volume');

  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: 'var(--text-primary)' }}>{position}</h3>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border-strong)', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-app)' }}>
              <SortHeader label="Player" sortKey="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortHeader label="Technical" sortKey="pillarScore" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
              {kpiCols.map(k => (
                <SortHeader
                  key={k.kpiId}
                  label={k.scoringMode === 'volume' ? `${k.kpiName} *` : k.scoringMode === 'bonus' ? `${k.kpiName} +` : k.kpiName}
                  sortKey={`kpi_${k.kpiId}`}
                  activeKey={sortKey}
                  dir={sortDir}
                  onClick={toggleSort}
                  align="right"
                  title={k.scoringMode === 'volume' ? 'Volume-scored — see note below'
                    : k.scoringMode === 'bonus' ? `Raw count · +${k.pointsPerUnit ?? '?'} Technical each, averaged across matches played`
                    : undefined}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(r => (
              <tr key={r.player_id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '9px 10px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                  {r.name}
                  <BonusBadges bonuses={r.bonuses} />
                </td>
                <ScoreCell value={r.pillarScore} bold />
                {kpiCols.map(k => (
                  <KpiCell key={k.kpiId} value={r[`kpi_${k.kpiId}`]} count={r[`kpi_${k.kpiId}_count`]} scoringMode={k.scoringMode} />
                ))}
              </tr>
            ))}
            {!sortedRows.length && (
              <tr><td colSpan={2 + kpiCols.length} style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 13 }}>No active players at this position.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {hasVolumeCols && (
        <p style={{ fontSize: 11, color: 'var(--text-dimmed)', margin: '6px 2px 0' }}>
          * Volume-scored (no SciSports success rate exists for this KPI) — shown as a plain raw count, not a percentage. No real per-position benchmark exists yet, so it's excluded from the Technical score until one is set.
        </p>
      )}
      {/* Legend built from the position's own bonus KPIs and their real per-unit weights, rather
          than a hardcoded goal/assist pair — each position carries its own set. */}
      {bonusKpis.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-dimmed)', margin: '4px 2px 0', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>+ Bonus-scored:</span>
          {bonusKpis.map(k => {
            const st = BONUS_STYLE[k.kpiName] || { abbr: k.kpiName.slice(0, 2).toUpperCase(), color: 'var(--text-muted)' };
            const Icon = st.icon;
            return (
              <span key={k.kpiId} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                {Icon ? <Icon size={11} color={st.color} /> : <span style={{ fontSize: 9.5, fontWeight: 700, color: st.color }}>{st.abbr}</span>}
                +{k.pointsPerUnit ?? '?'} per {BONUS_UNIT[k.kpiName] || k.kpiName.toLowerCase()}
              </span>
            );
          })}
          — each match's own bonus is added to that match's score, then the matches are averaged.
        </p>
      )}
    </div>
  );
}

export default function TechnicalOverview({ user, onNavigate }) {
  const [period, setPeriod] = useState('week');
  const [matchOptions, setMatchOptions] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDefinitions, setShowDefinitions] = useState(false);

  // Match picker options — reuses the same "which matches have Technical data" list the
  // Upload Zone's Technical Stats card already fetches (GET /api/technical-stats), rather
  // than a second endpoint for the same underlying question.
  useEffect(() => {
    getTechnicalStats()
      .then(data => setMatchOptions((data || []).map(m => ({
        value: String(m.match_id),
        label: `${m.opponent_name || m.session_title || 'Match'} · ${(m.session_date || '').slice(0, 10)}`,
      }))))
      .catch(() => setMatchOptions([]));
  }, []);

  // A match selection entirely overrides the period toggle — see getTechnicalKpiBreakdown/
  // computeTechnicalBreakdown's comments. One selected match shows that game's own numbers;
  // several show the pooled average across just those games.
  const matchIdsNum = useMemo(() => selectedMatchIds.map(Number), [selectedMatchIds]);

  useEffect(() => {
    setLoading(true);
    getTechnicalKpiBreakdown(period, matchIdsNum)
      .then(data => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period, matchIdsNum]);

  const groups = useMemo(() => {
    const g = {};
    POSITIONS.forEach(pos => { g[pos] = []; });
    rows.forEach(r => { if (g[r.position]) g[r.position].push(r); });
    return g;
  }, [rows]);

  const usingMatchSelection = matchIdsNum.length > 0;

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
      <div style={{ width: 'calc(100% - 76px)', margin: '28px 38px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: ACCENT }}>360 View — Technical Team Overview</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => onNavigate('technical-comparison')}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              <GitCompare size={14} /> Comparison
            </button>
            <button onClick={() => setShowDefinitions(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              <BookOpen size={14} /> Definitions &amp; Mapping
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8, opacity: usingMatchSelection ? 0.4 : 1, pointerEvents: usingMatchSelection ? 'none' : 'auto' }}>
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${period === p.value ? ACCENT : 'var(--border-strong)'}`, background: period === p.value ? ACCENT : 'transparent', color: period === p.value ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {p.label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>or</span>
          <MultiSelect options={matchOptions} selected={selectedMatchIds} onChange={setSelectedMatchIds} placeholder="Pick specific match(es)…" />
          {usingMatchSelection && (
            <button onClick={() => setSelectedMatchIds([])} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
              Clear
            </button>
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-dimmed)', margin: '0 0 32px' }}>
          {usingMatchSelection
            ? (matchIdsNum.length === 1 ? 'Showing that match’s own numbers.' : `Showing the average across these ${matchIdsNum.length} selected matches.`)
            : 'Pick one or more specific matches to see exact/average scores for just those games, instead of a week/28-day/season window.'}
        </p>

        {loading && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>}

        {!loading && POSITIONS.map(pos => (
          <PositionTable key={pos} position={pos} players={groups[pos]} />
        ))}
      </div>
      {showDefinitions && <DefinitionsModal onClose={() => setShowDefinitions(false)} />}
    </PageChrome>
  );
}
